import { NSCard } from 'bricks';
import classNames from 'classnames';
import RenderIf from 'components/RenderIf/RenderIf';
import { DealTypeToId } from 'constants/deals';
import { percentFormatter } from 'ns_libs/formatter';
import { useEffect, useState } from 'react';
import { IUploadedProForma } from 'types/db/UploadedProForma';
import { PREVIEW_RETURNS_FIELDS, ProFormaMappingRowNames } from 'views/ProFormaUpload/constants';
import { ILabelValueRow, IProFormaMappingRow, IUploadedProFormaReturns } from 'views/ProFormaUpload/types';
import './UploadedProFormaViewOnly.scss';
import { formatCurrencyWithParentheses } from 'views/ProFormaTable/helpers';
import { useLocation, useNavigate } from 'react-router-dom';

export interface IUploadedProFormaReadOnly {
    uploadedProForma: IUploadedProForma;
    mapping: Partial<Record<string, IProFormaMappingRow[]>>;
}

const UploadedProFormaViewOnly = ({ uploadedProForma, mapping }: IUploadedProFormaReadOnly) => {
    const location = useLocation();
    const navigate = useNavigate();

    const [returns, setReturns] = useState<IUploadedProFormaReturns[]>([]);

    const defaultSources = (mapping.developmentSources || []).map(source => ({ id: Number(source.id), name: source.name, amount: 0 }));
    const sources = uploadedProForma?.sources?.length ? uploadedProForma.sources : defaultSources;

    const LabelValueRow = ({ label, value }: ILabelValueRow) => {
        let formattedValue = '—';

        if (value !== null && value !== undefined) {
            formattedValue = formatCurrencyWithParentheses(value, true);
            if (formattedValue.endsWith('.00')) {
                formattedValue = formattedValue.slice(0, -3);
            }
        }

        return (
            <div className="d-flex justify-content-between">
                <span className="text-muted mb-1">{label}</span>
                <span className={classNames({
                    'text-dark': value !== null && value >= 0,
                    'font-weight-bold': [
                        ProFormaMappingRowNames.CASH_FLOW_FROM_OPS,
                        ProFormaMappingRowNames.TOTAL_USES,
                        ProFormaMappingRowNames.TOTAL_SOURCES,
                    ].includes(label as ProFormaMappingRowNames),
                })}
                >
                    {formattedValue}
                </span>
            </div>
        );
    };

    useEffect(() => {
        if (uploadedProForma) {
            setReturns([
                { title: PREVIEW_RETURNS_FIELDS.UNLEVERED_IRR, value: uploadedProForma?.unleveredIrr },
                { title: PREVIEW_RETURNS_FIELDS.GENERAL_PARTNER_IRR, value: uploadedProForma?.generalPartnerIrr },
                { title: PREVIEW_RETURNS_FIELDS.UNTRENDED_YIELD_ON_COST, value: uploadedProForma?.untrendedYieldOnCost },
                { title: PREVIEW_RETURNS_FIELDS.LEVERED_IRR, value: uploadedProForma?.leveredIrr },
                { title: PREVIEW_RETURNS_FIELDS.LIMITED_PARTNER_IRR, value: uploadedProForma?.limitedPartnerIrr },
                { title: PREVIEW_RETURNS_FIELDS.TRENDED_YIELD_ON_COST, value: uploadedProForma?.trendedYieldOnCost },
            ]);
        }
    }, [uploadedProForma]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get('shouldEdit') === 'true') {
            params.delete('shouldEdit');

            navigate({
                pathname: location.pathname,
                search: params.toString(),
            }, { replace: true });
        }
    }, [location, navigate]);

    return (
        <div className="w-50 d-flex flex-column">
            <div className="mb-2">
                <div className="text-muted mb-1 h6">RETURNS</div>
                <div className="UploadedProFormaViewOnly__returns-grid">
                    {returns.map(item => (
                        <div key={item.title} className="bg-secondary p-2 rounded">
                            <div className="text-muted text-truncate w-100 h6 mt-0">{item.title}</div>
                            <h3 className="text-white text-truncate m-0 w-100">
                                {item.value !== null && item.value !== undefined ? percentFormatter(item.value * 100) : '—'}
                            </h3>
                        </div>
                    ))}
                </div>
            </div>
            <div className="separator mb-3" />

            <div className="text-muted mb-1 h6 mt-0">OPERATIONS</div>
            <NSCard>
                <div className="p-2">
                    <LabelValueRow label="Effective gross revenue" value={uploadedProForma?.effectiveGrossRevenue} />
                    <LabelValueRow label="Total operating expenses" value={uploadedProForma?.totalOperatingExpenses} />
                    <div className="separator mt-1 mb-2" />
                    <LabelValueRow label="Net operating income" value={uploadedProForma?.netOperatingIncome} />
                    <LabelValueRow label="Capital expenditures" value={uploadedProForma?.capitalExpenditures} />
                    <div className="separator mt-1 mb-2" />
                    <LabelValueRow label="Cash flow from operations" value={uploadedProForma?.cashFlowFromOps} />
                </div>
            </NSCard>

            <RenderIf isTrue={uploadedProForma?.dealTypeId === DealTypeToId.Development}>
                <div className="text-muted mb-1 h6 mt-0">USES</div>
                <NSCard>
                    <div className="p-2">
                        <LabelValueRow label="Acquisition costs" value={uploadedProForma?.acquisitionCosts} />
                        <LabelValueRow label="Hard costs" value={uploadedProForma?.hardCosts} />
                        <LabelValueRow label="Soft costs" value={uploadedProForma?.softCosts} />
                        <LabelValueRow label="Financing costs" value={uploadedProForma?.financingCosts} />
                        <div className="separator mt-1 mb-2" />
                        <LabelValueRow label="Total uses" value={uploadedProForma?.totalUses} />
                    </div>
                </NSCard>
                <div className="text-muted mb-1 h6 mt-0">SOURCES</div>
                <NSCard>
                    <div className="p-2">
                        {sources?.map(source => (
                            <>
                                <RenderIf isTrue={source.name === ProFormaMappingRowNames.TOTAL_SOURCES}>
                                    <div className="separator mt-1 mb-2" />
                                </RenderIf>
                                <LabelValueRow key={source.id} label={source.name} value={source.amount} />
                            </>
                        ))}
                    </div>
                </NSCard>
            </RenderIf>
            <RenderIf isTrue={uploadedProForma?.dealTypeId === DealTypeToId.Acquisition}>
                <div className="text-muted mb-1 h6 mt-0">ACQUISITION</div>
                <NSCard>
                    <div className="p-2 mb-0">
                        <LabelValueRow label="Acquisition costs" value={uploadedProForma?.acquisitionCosts} />
                    </div>
                </NSCard>
            </RenderIf>
        </div>
    );
};

export default UploadedProFormaViewOnly;
